import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QUIZ_QUESTIONS, DIMENSION_WHY } from './quizData';
import { MinimalClock, MinimalTarget, MinimalChart } from './QuizIcons';
import './Quiz.css';

const Quiz = () => {
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [showEncouragement, setShowEncouragement] = useState(false);
  const [encouragementMessage, setEncouragementMessage] = useState('');
  const [showWhy, setShowWhy] = useState(false);

  const currentQuestion = QUIZ_QUESTIONS[currentQuestionIndex];
  const progress = ((currentQuestionIndex) / QUIZ_QUESTIONS.length) * 100;
  const totalQuestions = QUIZ_QUESTIONS.length;

  useEffect(() => {
    // Check if answer exists for current question
    const existingAnswer = answers[currentQuestion?.id];
    if (existingAnswer !== undefined) {
      setSelectedOption(existingAnswer);
    } else {
      setSelectedOption(null);
    }
    // Reset "Why we ask" when question changes
    setShowWhy(false);
  }, [currentQuestionIndex, currentQuestion, answers]);

  const handleStartQuiz = () => {
    setShowWelcome(false);
  };

  const handleOptionSelect = (optionValue) => {
    if (isAnimating) return;
    
    setSelectedOption(optionValue);
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: optionValue
    }));

    // Auto-advance after brief delay
    setTimeout(() => {
      handleNext();
    }, 400);
  };

  const handleNext = () => {
    if (isAnimating) return;

    setIsAnimating(true);
    
    setTimeout(() => {
      if (currentQuestionIndex < QUIZ_QUESTIONS.length - 1) {
        const nextIndex = currentQuestionIndex + 1;
        setCurrentQuestionIndex(nextIndex);
        
        // Show encouragement at 1/3 and 2/3 marks
        if (nextIndex === Math.floor(totalQuestions / 3)) {
          setEncouragementMessage("You're doing great! 💫");
          setShowEncouragement(true);
          setTimeout(() => setShowEncouragement(false), 2000);
        } else if (nextIndex === Math.floor(2 * totalQuestions / 3)) {
          setEncouragementMessage("Almost there! Keep going 🌟");
          setShowEncouragement(true);
          setTimeout(() => setShowEncouragement(false), 2000);
        }
      } else {
        // Calculate results and navigate
        calculateAndNavigateToResults();
      }
      setIsAnimating(false);
    }, 300);
  };

  const handleBack = () => {
    if (isAnimating || currentQuestionIndex === 0) return;
    
    setIsAnimating(true);
    
    setTimeout(() => {
      setCurrentQuestionIndex(prev => prev - 1);
      setIsAnimating(false);
    }, 300);
  };

  const calculateAndNavigateToResults = () => {
    // Calculate dimension scores
    const dimensionScores = {};
    
    QUIZ_QUESTIONS.forEach(question => {
      const answer = answers[question.id];
      if (answer !== undefined) {
        if (!dimensionScores[question.dimension]) {
          dimensionScores[question.dimension] = 0;
        }
        dimensionScores[question.dimension] += answer;
      }
    });

    // Navigate to results with scores
    navigate('/quiz-results', { state: { scores: dimensionScores } });
  };

  if (showWelcome) {
    return (
      <div className="quiz-container">
        <div className="quiz-welcome">
          <div className="welcome-content">
            <h1 className="welcome-title">Discover Your Archetype</h1>
            <p className="welcome-subtitle">
              Discover who you are and who you want to become
            </p>
            
            <div className="welcome-features">
              <div className="feature-item">
                <MinimalClock />
                <div className="feature-text">
                  <strong>7-10 minutes</strong>
                  <span>Quick but meaningful</span>
                </div>
              </div>
              
              <div className="feature-item">
                <MinimalTarget />
                <div className="feature-text">
                  <strong>42 questions</strong>
                  <span>Exploring 7 core dimensions</span>
                </div>
              </div>
              
              <div className="feature-item">
                <MinimalChart />
                <div className="feature-text">
                  <strong>Visual results</strong>
                  <span>See your unique value map</span>
                </div>
              </div>
            </div>

            <div className="science-section">
              <h3 className="science-title">Grounded in Research</h3>
              <p className="science-description">
                This assessment draws from validated frameworks in value psychology and personality research:
              </p>
              <div className="science-frameworks">
                <div className="framework-item">
                  <strong>Schwartz Value Theory</strong>
                  <span>10 universal human values across 80+ countries</span>
                </div>
                <div className="framework-item">
                  <strong>Self-Determination Theory</strong>
                  <span>Core needs: autonomy, competence, and connection</span>
                </div>
                <div className="framework-item">
                  <strong>Moral Foundations Theory</strong>
                  <span>Psychological roots of moral instincts</span>
                </div>
              </div>
              <p className="science-value">
                Your results provide a <strong>reflective baseline</strong> for understanding your core values—not a fixed identity, but a map for self-discovery and growth.
              </p>
            </div>

            <button className="quiz-start-button" onClick={handleStartQuiz}>
              Start Quiz
            </button>

            <p className="welcome-note">
              There are no right or wrong answers. Be honest with yourself.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-container">
      {/* Progress Bar */}
      <div className="quiz-progress-container">
        <div className="quiz-progress-bar">
          <div 
            className="quiz-progress-fill" 
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="quiz-progress-text">
          {currentQuestionIndex} of {totalQuestions}
        </div>
      </div>

      {/* Question Card */}
      <div className={`quiz-question-card ${isAnimating ? 'animating' : ''}`}>
        <div className="quiz-question-header">
          <h2 className="quiz-question-title">{currentQuestion.question}</h2>
          <p className="quiz-question-subtitle">{currentQuestion.subtitle}</p>
        </div>

        <div className="quiz-options">
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              className={`quiz-option ${
                selectedOption === option.value ? 'selected' : ''
              }`}
              onClick={() => handleOptionSelect(option.value)}
            >
              <span className="option-text">{option.text}</span>
              {selectedOption === option.value && (
                <span className="option-check">✓</span>
              )}
            </button>
          ))}
        </div>

        {/* Navigation */}
        <div className="quiz-navigation">
          <button
            className="quiz-nav-button quiz-back-button"
            onClick={handleBack}
            disabled={currentQuestionIndex === 0}
          >
            ← Back
          </button>
          
          <div className="quiz-dimension-indicator">
            {currentQuestion.dimension.replace('↔', ' vs. ')}
          </div>
        </div>

        {/* Why We Ask - Collapsible Footer */}
        <div className="quiz-why-footer">
          <button 
            className="quiz-why-toggle"
            onClick={() => setShowWhy(!showWhy)}
            aria-expanded={showWhy}
          >
            <span className="quiz-why-toggle-text">
              Why we ask: {currentQuestion.dimension.replace('↔', ' vs. ')}
            </span>
            <span className={`quiz-why-toggle-icon ${showWhy ? 'expanded' : ''}`}>
              {showWhy ? '−' : '+'}
            </span>
          </button>
          
          {showWhy && (
            <div className="quiz-why-content">
              <p className="quiz-why-text">{DIMENSION_WHY[currentQuestion.dimension]}</p>
            </div>
          )}
        </div>
      </div>

      {/* Encouraging Messages */}
      {showEncouragement && (
        <div className="quiz-encouragement">
          <p>{encouragementMessage}</p>
        </div>
      )}
    </div>
  );
};

export default Quiz;

